import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationService, GasService } from '@app/core';
import { FiatService } from '@app/core/account/fiat.service';
import { Subscription } from 'rxjs/Subscription';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { ModalService } from '@app/shared/modal.service';
import { WithdrawService } from '@app/core/account/withdraw.service';
import { ErrorManagerService } from 'app/core/error-manager.service';
import { Response } from '@angular/http';
import { AlertService } from '@app/core/alert.service';
import * as amplitude from 'amplitude-js';
import { BalanceService, CurrencyType } from '@app/core/balance.service';
import { CURRENCY_OPTIONS } from '@app/core/constants';
import { validateWallet } from '@app/shared/validators/wallet.validator';
import { IntercomService } from '@app/core/intercom.service';

@Component({
  selector: 'app-withdraw-2-modal',
  template: `
    <div class="modal-column">
      <form (ngSubmit)="preSubmit()" [formGroup]="form" style="width: 100%;">
        <app-currency-selector formControlName="currency" [ignoreCoins]="['usd']"></app-currency-selector>

        <div class="dw-tabs-menu">
          <button class="dw-tabs-item" [routerLink]="['/', { outlets: { modal: 'deposit' } }]">
            <span class="arrow-icon"><i class="fal fa-arrow-up"></i></span> Deposit
          </button>
          <button class="dw-tabs-item active">
            <span class="arrow-icon"> <i class="fal fa-arrow-down"></i> </span> Withdraw
          </button>
        </div>

        <ng-container *ngIf="!summary">
          <h5>Amount to withdraw</h5>
          <div class="amount-to-withdraw-sidelfex">
            <div class="withdraw-input-wrap">
              <input
                type="number"
                formControlName="amount"
                class="input"
                maxlength="25"
                name="amount-to-withdraw"
                [min]="inputStep"
                [step]="inputStep"
                required
                placeholder="0.0000"
              />
              <div class="btc-amount">{{ currency }}</div>
            </div>
            <div>≈</div>
            <div class="withdraw-amount-conversion">
              <div>{{ amountToUsd | currency: 'USD' }}</div>
              <div>Current Rate: {{ 1 | crypto: currency }} = {{ usdRate | currency: 'USD' }}</div>
            </div>
          </div>

          <div class="deposit-limits">
            Maximum withdrawal amount is
            <span class="deposit-limit-feature">{{ maxAmount | crypto: currency }}</span> in 24 hours. Minimum withdraw
            amount is <span class="deposit-limit-feature">{{ minAmount | crypto: currency }}</span
            >.
          </div>

          <div class="_25-px-spacer"></div>

          <div class="input-label">Your wallet address</div>

          <input
            type="text"
            class="input full"
            maxlength="256"
            formControlName="address"
            name="walletAddress"
            placeholder="Paste your {{ currency }} wallet address"
            required
          />

          <button type="button" class="button" [disabled]="!allowSummary()" (click)="summary = true">
            Make a withdrawal
          </button>

          <ng-container *ngIf="currency === 'DAI'">
            <br /><br />
            <p>
              Or <a href="/" (click)="openSupport($event)">contact support</a> if you want to withdraw USD using wire
              transfer.
            </p>
          </ng-container>
        </ng-container>

        <ng-container *ngIf="summary">
          <h5>Withdrawal summary</h5>
          <div class="withdrawal-sideflex">
            <div>{{ currency }} amount</div>
            <div>
              <strong>{{ form.get('amount').value | crypto: currency }}</strong>
            </div>
          </div>
          <div class="withdrawal-sideflex">
            <div>Processing fee</div>
            <div>
              <strong>{{ feeAmount | crypto: currency }}</strong>
            </div>
          </div>
          <div class="withdrawal-sideflex total">
            <div>Total amount</div>
            <div>
              <strong>{{ form.get('amount').value - feeAmount | crypto: currency }}</strong>
            </div>
          </div>
          <div class="_25-px-spacer"></div>
          <div class="input-label">Your wallet address</div>
          <div class="deposit-address-wrap">
            <div>{{ form.get('address').value }}</div>
          </div>
          <div class="_10-px-spacer"></div>
          <div class="input-label">Confirm password</div>
          <input
            type="password"
            class="input full"
            formControlName="password"
            maxlength="256"
            name="password"
            data-name="Enter your password"
            placeholder="Enter your password"
            id="Enter-your-password"
            required=""
          />
          <div class="withdrawal-sidefelx-buttons">
            <button type="button" class="button button-transparent" (click)="summary = false">Go back</button>
            <div class="_20-px-spacer"></div>
            <button type="submit" class="button" [disabled]="form.invalid || loading">Confirm withdrawal</button>
          </div>
        </ng-container>
      </form>
    </div>
    <a [routerLink]="['/', { outlets: { modal: null } }]" class="icon close-popup" data-id="withdraw-close-href">
      <i class="fal fa-times"></i>
    </a>
  `,
  styleUrls: ['./tabs.scss', './deposit-modal-2.template.scss'],
})
export class WithdrawModal2Component implements OnInit, OnDestroy {
  public form: FormGroup;
  public currency: string = this.balance
    .getCurrency()
    .toString()
    .toUpperCase();
  public minAmount: number = 0;
  public maxAmount: number = 0;
  public feeAmount: number = 0;
  public inputStep: number = 1;
  public available: number = 0;

  public amountToUsd: number = 0;
  public usdRate: number = 0;

  public summary: boolean = false;
  public loading: boolean = false;

  private subs: Subscription = new Subscription();
  private limits$: Subscription;

  constructor(
    private router: Router,
    public auth: AuthenticationService,
    private formBuilder: FormBuilder,
    private gas: GasService,
    private withdraw: WithdrawService,
    private fiatService: FiatService,
    private sanitizer: DomSanitizer,
    private modalService: ModalService,
    private alertService: AlertService,
    private errorManager: ErrorManagerService,
    private balance: BalanceService,
    private intercom: IntercomService
  ) {}

  ngOnInit() {
    this.createForm();
    this.init(this.auth.userInfo);
    this.updateCurrencyData();
  }

  init(user: any): void {
    if (user.verified !== 'true') {
      if (!user.emailVerified) {
        this.alertService.error({
          title: 'Notification',
          text: 'You need to verify your email to withdraw balance.',
          timer: 10,
        });
        this.modalService
          .openAlert({
            title: 'Confirm your email',
            text: 'Please complete your email confirmation to enable withdrawals.',
            ok: 'Verify email',
            cancel: 'Later',
          })
          .result.then(() => {
            this.router.navigate(['/', { outlets: { modal: 'signup-verify' } }]);
          })
          .catch(() => {
            this.router.navigate(['/', { outlets: { modal: null } }]);
          });
      } else {
        this.modalService
          .openAlert({
            title: 'Last step',
            text:
              'Edgeless service is not available for minors younger than 18.\n' +
              ' Platform has to make sure you are older than 18 years old.' +
              ' Please complete KYC procedure to enable withdrawals.',
            ok: 'Complete KYC',
            cancel: 'Later',
          })
          .result.then(() => {
            this.router.navigate(['/', { outlets: { modal: 'signup-kyc' } }]);
          })
          .catch(() => {
            this.router.navigate(['/', { outlets: { modal: null } }]);
          });
      }
    }
  }

  private createForm() {
    this.form = this.formBuilder.group({
      currency: this.balance.getCurrency(),
      amount: [null, [Validators.required]],
      address: [null, [Validators.required, validateWallet(this.balance.getCurrency())]],
      password: [null, [Validators.required]],
    });

    this.subs.add(
      this.form.get('currency').valueChanges.subscribe(currency => {
        this.currency = currency.toString().toUpperCase();
        if (this.summary) {
          this.summary = false;
        }
        this.updateCurrencyData();
      })
    );

    this.subs.add(
      this.form.get('amount').valueChanges.subscribe(() => {
        this.updateAmountData();
      })
    );
  }

  private updateCurrencyData(): void {
    const options = CURRENCY_OPTIONS[this.currency.toLowerCase()];
    if (options) {
      this.inputStep = options.visibleDecimals > 1 ? Number('0.' + '0'.repeat(options.visibleDecimals) + '1') : 1;
    }

    if (this.limits$) {
      this.limits$.unsubscribe();
    }

    this.limits$ = this.withdraw.withdrawLimits(this.currency.toLowerCase()).subscribe(
      limits => {
        this.minAmount = limits.min;
        this.maxAmount = limits.max;
        this.feeAmount = limits.fee;
        this.available = limits.availableBalance;
        this.usdRate = limits.usdRate;
        this.updateAmountData();
        if (this.form) {
          const _amount = this.form.get('amount');
          _amount.setValue(this.floatAmount(Math.min(this.available, this.minAmount), this.currency));
          _amount.clearValidators();
          _amount.setValidators([
            Validators.required,
            Validators.min(this.minAmount),
            Validators.max(Math.min(this.maxAmount, this.available)),
          ]);
          const _wallet = this.form.get('address');
          _wallet.clearValidators();
          _wallet.setValidators([Validators.required, validateWallet(this.currency)]);
          _amount.updateValueAndValidity();
          _wallet.updateValueAndValidity();
        }
      },
      (error: Error) => {
        //
      }
    );
  }

  private updateAmountData(): void {
    this.amountToUsd = this.form.get('amount').value * this.usdRate;
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    if (this.limits$) {
      this.limits$.unsubscribe();
    }
  }

  preSubmit(): void {
    if (this.loading) {
      return;
    }
    this.modalService
      .openAlert({
        title: 'One more thing...',
        text:
          'For security reasons, you will need to confirm your request by clicking' +
          ' on the link we will send to your email address.<br> Link will be valid for 30 minutes.',
        ok: 'Understood! Send me an email',
      })
      .result.then(() => {
        this.submit();
      })
      .catch(() => {
        this.loading = false;
      });
  }

  submit(): void {
    if (this.loading) {
      return;
    }

    const { currency, amount } = this.form.value;
    this.loading = true;

    amplitude.getInstance().logEvent('Withdrawal - Withdraw', {
      Currency: currency,
      Amount: amount,
    });

    this.withdraw
      .requestWithdraw(this.form.value)
      .then((response: Response) => {
        this.loading = false;
        this.summary = false;
        this.form.reset({
          currency: this.form.get('currency').value,
          amount: this.form.get('amount').value,
        });
        this.modalService.openAlert({
          title: 'Success',
          text: 'We have sent you an email, please confirm it within 30 minutes to approve your withdrawal.',
        });
      })
      .catch((error: Response) => {
        this.alertService.error({ title: 'Withdraw error!', text: this.errorManager.errorToPrint(error.json()) });
        this.loading = false;
      });
  }

  public allowSummary(): boolean {
    return this.form.get('amount').valid && this.form.get('address').valid;
  }

  public openSupport($event: Event) {
    $event.preventDefault();
    this.intercom.show();
  }

  private floatAmount(amount: number, currency: string): number {
    const option = CURRENCY_OPTIONS[currency.toLowerCase()];
    const multiplier = Number('1' + '0'.repeat(option.visibleDecimals));
    return Math.floor(amount * multiplier) / multiplier;
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Prod_web-client/src/app/_v2/template/modals/deposit/withdraw-modal-2.component.ts